import { React, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaSun, FaMoon, FaAdjust } from 'react-icons/fa';
import Logo from '../assets/Logos/Logo-Excent-White.svg';
import { useLogin } from '../components/logIncontext';
import { BsGlobe } from "react-icons/bs";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { PiUser } from "react-icons/pi";
import enIcon from '../assets/Languages/en.svg';
import esIcon from '../assets/Languages/es.svg';
import ptIcon from '../assets/Languages/pt.svg';
import frIcon from '../assets/Languages/fr.svg';
import itIcon from '../assets/Languages/it.svg';
import zhIcon from '../assets/Languages/zh.svg';
import { useNotifications } from "./Notifications";
import { dataCy, SELECTORS } from '../utils/dataCy';


import { getAccountsSignInUrl, getAccountsSignUpUrl, getBackofficeUrl, getCookieSuffix, getLogoutUrl, getMainpageUrl, getTradingUrl } from '../utils/resolveEnv';
const Menu = () => {
  const { t, i18n } = useTranslation('home'); // Namespace 'home' para traducciones del menú
  const navigate = useNavigate();
  const categories = t('categories', { returnObjects: true }) || [];

  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const categoriesRef = useRef(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false); // Estado para el menú hamburguesa
  const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState(false);

  const { isLoggedIn, userData } = useLogin();

  const { disconnect } = useNotifications();



  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("theme") || "auto";
  });


  // funcion para bloquear el scroll detras del menu

  useEffect(() => {
    if (isMenuOpen || isLanguageSelectorOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen, isLanguageSelectorOpen]);

  //  Funcion del tema oscuro
  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else if (theme === "light") {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    } else {
      // Detectar el esquema de color del sistema
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      document.documentElement.classList.toggle("dark", prefersDark);
      localStorage.setItem("theme", "auto");
    }
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : theme === "dark" ? "auto" : "light";
    setTheme(newTheme);
  };

  const [openCategories, setOpenCategories] = useState({});

  // Lista de idiomas disponibles
  const availableLanguages = [
    { code: 'en', icon: enIcon, label: t('en') },
    { code: 'es', icon: esIcon, label: t('es') },
    { code: 'pt', icon: ptIcon, label: t('pt') },
    { code: 'fr', icon: frIcon, label: t('fr') },
    { code: 'it', icon: itIcon, label: t('it') },
    { code: 'zh', icon: zhIcon, label: t('zh') },
  ];

  // Función para manejar la navegación
  const handleNavigate = (path = '') => {
    navigate(`/${i18n.language}${path}`);
    window.scrollTo(0, 0);
  };

  // Función para cambiar el idioma
  const changeLanguage = (newLang) => {
    const currentPath = location.pathname.split('/').slice(1); // [idioma, categoría, subcategoría?, artículo]
    const [currentLang, currentCategorySlug, currentSubcategoryOrArticle, currentArticleSlug] = currentPath;

    if (!currentCategorySlug || !currentSubcategoryOrArticle) {
      // Si estamos en el home, solo cambiamos el idioma
      i18n.changeLanguage(newLang);
      navigate(`/${newLang}/`);
      return;
    }

    // Usar t para obtener las categorías traducidas del idioma actual y del nuevo idioma
    const currentCategories = t('categories', { lng: currentLang, returnObjects: true }) || [];
    const newCategories = t('categories', { lng: newLang, returnObjects: true }) || [];

    // Crear un diccionario de traducción de slugs
    const slugTranslationMap = {};
    
    currentCategories.forEach((cat, catIndex) => {
      const newCat = newCategories[catIndex];
      if (newCat) {
        // Manejar artículos directos de la categoría
        if (cat.articles) {
          cat.articles.forEach((art, artIndex) => {
            const newArt = newCat.articles?.[artIndex];
            if (newArt) {
              slugTranslationMap[`${cat.slug}.${art.slug}`] = {
                newCategorySlug: newCat.slug,
                newArticleSlug: newArt.slug,
                isSubcategory: false
              };
            }
          });
        }
        
        // Manejar subcategorías
        if (cat.subcategories) {
          cat.subcategories.forEach((subcat, subcatIndex) => {
            const newSubcat = newCat.subcategories?.[subcatIndex];
            if (newSubcat && subcat.articles) {
              subcat.articles.forEach((art, artIndex) => {
                const newArt = newSubcat.articles?.[artIndex];
                if (newArt) {
                  slugTranslationMap[`${cat.slug}.${subcat.slug}.${art.slug}`] = {
                    newCategorySlug: newCat.slug,
                    newSubcategorySlug: newSubcat.slug,
                    newArticleSlug: newArt.slug,
                    isSubcategory: true
                  };
                }
              });
            }
          });
        }
      }
    });

    // Determinar si es una subcategoría o artículo directo
    let translationKey;
    let translatedSlugs;
    
    if (currentArticleSlug) {
      // Formato: /lang/category/subcategory/article
      translationKey = `${currentCategorySlug}.${currentSubcategoryOrArticle}.${currentArticleSlug}`;
      translatedSlugs = slugTranslationMap[translationKey];
    } else {
      // Formato: /lang/category/article
      translationKey = `${currentCategorySlug}.${currentSubcategoryOrArticle}`;
      translatedSlugs = slugTranslationMap[translationKey];
    }

    if (translatedSlugs) {
      i18n.changeLanguage(newLang);
      
      if (translatedSlugs.isSubcategory) {
        // Navegar con subcategoría
        navigate(`/${newLang}/${translatedSlugs.newCategorySlug}/${translatedSlugs.newSubcategorySlug}/${translatedSlugs.newArticleSlug}`);
      } else {
        // Navegar sin subcategoría
        navigate(`/${newLang}/${translatedSlugs.newCategorySlug}/${translatedSlugs.newArticleSlug}`);
      }
    } else {
      // Si no se encuentran traducciones, navegar al home en el nuevo idioma
      console.warn('No translation found for slugs. Redirecting to home.');
      i18n.changeLanguage(newLang);
      navigate(`/${newLang}/`);
    }
  };

  //Función para cerrar el comutador haciendo clic afuera
  const languageSwitcherRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      // Cerrar el selector de idioma si se hace clic fuera
      if (languageSwitcherRef.current && !languageSwitcherRef.current.contains(event.target)) {
        setIsLanguageSelectorOpen(false);
      }

      // Cerrar el dropdown de categorías si se hace clic fuera
      if (categoriesRef.current && !categoriesRef.current.contains(event.target)) {
        setIsCategoriesOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false); // Cierra el menú si el clic es fuera
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleCategory = (slug) => {
    setOpenCategories((prev) => ({
      ...prev,
      [slug]: !prev[slug],
    }));
  };

  // Función para redirigir a la página de inicio de sesión

  const handleGetMainpage = () => {
    let authBaseUrl;

    const hostname = window.location.hostname;

    authBaseUrl = getMainpageUrl(hostname);

    const finalUrl = `${authBaseUrl}`;

    window.location.href = finalUrl;
  };

  const handleGetStartedClickNav = () => {
    let authBaseUrl;

    const hostname = window.location.hostname;

    authBaseUrl = getAccountsSignInUrl(hostname);

    const path = window.location.pathname;
    const finalUrl = `${authBaseUrl}?platform=knowledge&origin=${encodeURIComponent(path)}`;

    window.location.href = finalUrl;
  };

  const handleGetStartedClickL = () => {
    let authBaseUrl;

    const hostname = window.location.hostname;

    authBaseUrl = getTradingUrl(hostname);

    const finalUrl = `${authBaseUrl}`;

    window.location.href = finalUrl;
  };

  const handleGetStartedClickUp = () => {
    let authBaseUrl;

    const hostname = window.location.hostname;

    authBaseUrl = getAccountsSignUpUrl(hostname);

    const path = window.location.pathname;
    const finalUrl = `${authBaseUrl}?platform=knowledge&origin=${encodeURIComponent(path)}`;

    window.location.href = finalUrl;
  };

  const handleGetStartedClickBo = () => {
    let authBaseUrl;

    const hostname = window.location.hostname;

    authBaseUrl = getBackofficeUrl(hostname);

    const finalUrl = `${authBaseUrl}`;

    window.location.href = finalUrl;
  };


  // Logica de inicio de sesión
  const handleClick = isLoggedIn ? handleGetStartedClickL : handleGetStartedClickUp;

  const [isLogInOpen, setIsLogInOpen] = useState(false);
  const logInTimeoutRef = useRef(null);

  const handleMouseEnterL = () => {
    clearTimeout(logInTimeoutRef.current);
    logInTimeoutRef.current = setTimeout(() => setIsLogInOpen(true), 100);
  };

  const handleMouseLeaveL = () => {
    clearTimeout(logInTimeoutRef.current);
    logInTimeoutRef.current = setTimeout(() => setIsLogInOpen(false), 100);
  };

  const handleLogout = async () => {
    disconnect("logout");
    try {
      const hostname = window.location.hostname;

      // Detectar nombre de cookie según entorno
      const cookieName = `refresh${getCookieSuffix(hostname)}`;

      // Obtener y parsear cookie
      const cookieMatch = document.cookie.match(new RegExp(`(?:^|;\\s*)${cookieName}=([^;]+)`));
      if (cookieMatch) {
        const raw = decodeURIComponent(cookieMatch[1]);
        let access = null;
        try {
          const refreshObj = JSON.parse(raw);
          access = refreshObj?.access;
        } catch {
          // si no es JSON, simplemente no tendremos access
        }

        if (access) {
          let logOutUrl;
          logOutUrl = getLogoutUrl(hostname);

          const res = await fetch(logOutUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${access}`,
            },
          });

          if (!res.ok) {
            console.warn('⚠️ Logout API respondió con error:', res.status);
          } else {
            console.log('✅ Logout exitoso con la API.');
          }
        }
      } else {
        console.warn(`⚠️ No se encontró la cookie "${cookieName}" al intentar cerrar sesión.`);
      }

      // 🧹 Intentar matar todas las variantes de nombre
      killCookieEverywhere(['refresh', 'refresh_dev', 'refresh_test', 'refresh_demo']);

      // 🧹 Limpiar almacenamiento
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      sessionStorage.removeItem('mobileReloaded');
      localStorage.removeItem('lastRefreshCookie');
      localStorage.removeItem('needsReload');
      localStorage.removeItem('lastReload');
    } catch (error) {
      console.error('❌ Error al hacer logout:', error);
    }

    // 🔄 Recargar página
    window.location.reload();
  };

  // ---- util ----
  function killCookieEverywhere(names) {
    const host = window.location.hostname;     // p.ej. knowledgebase.dev.excent.capital
    const parts = host.split('.');
    const domainCandidates = new Set();

    // host-only (sin Domain=)
    domainCandidates.add(null);

    // Todos los padres posibles
    for (let i = 0; i < parts.length - 1; i++) {
      domainCandidates.add(parts.slice(i).join('.')); // knowledgebase.dev.excent.capital, dev.excent.capital, excent.capital
    }
    // Raíces recurrentes
    domainCandidates.add('excent.capital');
    domainCandidates.add('excentcapital.com');
    domainCandidates.add('.excent.capital');
    domainCandidates.add('.excentcapital.com');

    // Paths desde la ruta actual hacia arriba + comunes
    const path = window.location.pathname || '/';
    const segments = path.split('/').filter(Boolean);
    const paths = new Set(['/']);
    let acc = '';
    segments.forEach(seg => {
      acc += '/' + seg;
      paths.add(acc);
    });
    ['/auth', '/accounts', '/login', '/logout'].forEach(p => paths.add(p));

    const variants = [
      '',
      'SameSite=Lax',
      'SameSite=None; Secure'
    ];

    const expires = 'Expires=Thu, 01 Jan 1970 00:00:00 GMT';
    const maxAge0 = 'Max-Age=0';

    names.forEach(name => {
      domainCandidates.forEach(domain => {
        paths.forEach(p => {
          variants.forEach(v => {
            const domainAttr = domain ? `; domain=${domain}` : '';
            document.cookie = `${name}=; ${expires}; ${maxAge0}; path=${p}${domainAttr}${v ? '; ' + v : ''}`;
          });
        });
      });
      // Host-only básico
      document.cookie = `${name}=; ${expires}; ${maxAge0}; path=/`;
    });
  }


  return (
    <>
      <nav style={{ top: 'var(--risk-warning-height, 0px)' }} className="fixed left-0 right-0 z-[99999] flex items-center justify-between  md:justify px-6 sm:px-28 md:px-16 h-24 bg-primaryBg dark:bg-blueMenu text-white w-full scrollbar-hide">


        {/* Barra superior en desktop */}
        {/* Logo */}
        <div
          className="flex-none cursor-pointer"
          onClick={() => handleNavigate('/')}
          data-cy={dataCy(SELECTORS.DIV, 'menu-logo')}
        >
          <img src={Logo} className="h-8 sm:h- md:h-8 lg:h-10" alt="Logo" />

        </div>
        <div className="hidden md:hidden lg:flex items-center gap-2">
          {/* Links del menú */}
          <div className="flex gap-4">
            <p className="cursor-pointer hover:bg-gray-200 hover:text-primaryBg dark:hover:text-white dark:hover:bg-grayButton px-4 py-2 rounded-lg transition" onClick={() => handleNavigate('/')} data-cy={dataCy(SELECTORS.BUTTON, 'menu-home')}>
              {t('navigation.home')}
            </p>

            {/* Dropdown de las categorias */}

            <div ref={categoriesRef} className="relative z-50">
              {/* Botón para abrir el dropdown */}
              <button
                className='hover:bg-gray-200 hover:text-primaryBg dark:hover:text-white dark:hover:bg-grayButton px-4 py-2 rounded-lg transition'
                onClick={() => setIsCategoriesOpen((prev) => !prev)}
                data-cy={dataCy(SELECTORS.BUTTON, 'menu-categories')}
              >
                {t('navigation.categories')}
              </button>

              {/* Dropdown con animación */}
              <AnimatePresence>
                {isCategoriesOpen && (
                  <motion.div
                    className="absolute top-full left-0 mt-2 bg-white/5 backdrop-blur-[30px] dark:bg-white/5 dark:backdrop-blur-[50px] text-black dark:text-white rounded-xl p-2 w-max z-50"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    {categories.filter(category => {
                      if (category.slug === 'api') {
                        const isLocalhost = window.location.hostname === 'localhost';
                        const apiEnabled = userData?.config?.apiEnabled;
                        return isLocalhost || apiEnabled;
                      }
                      const formulasSlugs = ['formulas', 'formules', 'formule', 'formulas-zh'];
                      if (formulasSlugs.includes(category.slug)) {
                        const isLocalhost = window.location.hostname === 'localhost';
                        const formulasEnabled = userData?.config?.formulasEnabled;
                        return isLocalhost || formulasEnabled;
                      }
                      return true;
                    }).map((category, index) => {
                      // Mostrar si tiene artículos directos o subcategorías
                      const hasArticles = category.articles && category.articles.length > 0;
                      const hasSubcategories = category.subcategories && category.subcategories.length > 0;
                      
                      if (!hasArticles && !hasSubcategories) return null;

                      // Función para encontrar el primer artículo disponible
                      const findFirstArticle = (cat) => {
                        // Si tiene artículos directos
                        if (cat.articles && cat.articles.length > 0) {
                          return { article: cat.articles[0], path: [cat.slug] };
                        }
                        
                        // Buscar en subcategorías
                        if (cat.subcategories) {
                          for (const sub of cat.subcategories) {
                            if (sub.articles && sub.articles.length > 0) {
                              return { article: sub.articles[0], path: [cat.slug, sub.slug] };
                            }
                            
                            // Buscar en subcategorías anidadas
                            if (sub.subcategories) {
                              for (const nestedSub of sub.subcategories) {
                                if (nestedSub.articles && nestedSub.articles.length > 0) {
                                  return { article: nestedSub.articles[0], path: [cat.slug, sub.slug, nestedSub.slug] };
                                }
                              }
                            }
                          }
                        }
                        return null;
                      };
                      
                      const firstArticleData = findFirstArticle(category);

                      return (
                        <div
                          key={index}
                          className="cursor-pointer hover:bg-gray-200 dark:hover:bg-grayButton px-4 py-3 rounded-lg transition"
                          onClick={() => {
                            if (firstArticleData) {
                              const pathSegments = [i18n.language, ...firstArticleData.path, firstArticleData.article.slug];
                              const url = `/${pathSegments.join('/')}`;
                              console.log('Navigating to:', url); // Debug
                              navigate(url);
                              setIsCategoriesOpen(false);
                              window.scrollTo(0, 0);
                            }
                          }}
                        >
                          {category.title}
                        </div>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>


            <a
              href="#"
              onClick={handleGetMainpage}
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer hover:bg-gray-200 dark:hover:text-white hover:text-primaryBg dark:hover:bg-grayButton px-4 py-2 rounded-lg transition"
              data-cy={dataCy(SELECTORS.LINK, 'menu-excent')}
            >
              {t('navigation.excentCapital')}
            </a>

            <p className="cursor-pointer hover:bg-gray-200 dark:hover:text-white hover:text-primaryBg dark:hover:bg-grayButton px-4 py-2 rounded-lg transition" onClick={() => handleNavigate('/contact/')} data-cy={dataCy(SELECTORS.BUTTON, 'menu-contact')}>
              {t('navigation.contact')}
            </p>
          </div>
        </div>

        {/* Botones de cuenta, idioma y modo oscuro */}
        <div className="hidden md:hidden lg:flex items-center gap-4">

          {/* Botones  de Open Account y Login */}
          <div className="flex items-center gap-2">
            {!isLoggedIn || !userData ? (
              <div className='flex flex-row gap-4'>
                <button
                  onClick={handleClick}
                  className="bg-[#086ef9] border-2 border-[#3288ff] text-white text-[0.9rem] px-[30px] py-[12px] rounded-lg hover:border-[#086ef9] transition-colors cursor-pointer"
                  data-cy={dataCy(SELECTORS.BUTTON, 'menu-signup')}
                >
                  {t('sign-up')}
                </button>
                <button
                  onClick={handleGetStartedClickNav}
                  className="bg-[#086ef9] border-2 border-[#3288ff] text-white text-[0.9rem] px-[30px] py-[12px] rounded-lg hover:border-[#086ef9] transition-colors cursor-pointer"
                  data-cy={dataCy(SELECTORS.BUTTON, 'menu-login')}
                >
                  {t('login')}
                </button>
              </div>
            ) : (
              <div className='flex flex-row gap-4'>
                {userData?.role !== "Client" && (
                  <button
                    onClick={handleGetStartedClickBo}
                    className="bg-[#086ef9] border-2 border-[#3288ff] text-white text-[0.9rem] px-[30px] py-[12px] rounded-lg hover:border-[#086ef9] transition-colors cursor-pointer"
                    data-cy={dataCy(SELECTORS.BUTTON, 'menu-backoffice')}
                  >
                    {t('backoffice')}
                  </button>
                )}

                <button
                  onClick={handleClick}
                  className="bg-[#086ef9] border-2 border-[#3288ff] text-white text-[0.9rem] px-[30px] py-[12px] rounded-lg hover:border-[#086ef9] transition-colors cursor-pointer"
                  data-cy={dataCy(SELECTORS.BUTTON, 'menu-myaccount')}
                >
                  {t('my-account')}
                </button>

                <div
                  className="relative"
                  onMouseEnter={handleMouseEnterL}
                  onMouseLeave={handleMouseLeaveL}
                >
                  <button
                    className="bg-[#086ef9] border-2 border-[#3288ff] text-white text-[0.9rem] px-3 py-3 rounded-[8px] cursor-pointer"
                    data-cy="ec-button-desktop-menu-user-icon"
                  >
                    <PiUser className='text-[1.3rem]' />
                  </button>

                  {isLogInOpen && (
                    <motion.div
                      data-cy="ec-div-desktop-menu-user-data-container"
                      className="
        absolute top-[calc(100%+20px)] right-0 left-auto    
        z-[1000] origin-top-right                          
        w-[250px] max-w-[min(90vw,20rem)]                 
        bg-white/5 p-7 rounded-[20px] shadow-lg backdrop-blur-[50px] text-white
      "
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <p className="text-base font-semibold mb-2">{userData.name}</p>
                      <div className="w-full border-t border-white"></div>
                      <div className="mb-3 mt-2">
                        <p className="text-sm font-semibold">{t('email')}</p>
                        <p className="text-sm text-gray-400 truncate">{userData.email}</p>
                      </div>
                      <button
                        data-cy="ec-button-desktop-menu-sign-out"
                        className="w-full text-sm text-red-400 border border-red-400 rounded-lg py-2 hover:bg-red-500 hover:text-white transition-colors duration-200 cursor-pointer"
                        onClick={handleLogout}
                      >
                        {t('logout')}
                      </button>
                    </motion.div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Conmutador de tema */}
          <button
            onClick={toggleTheme}
            className="border-2 border-grayButtonBorder bg-grayButton rounded-lg text-white px-3 py-3 flex items-center justify-center hover:border-white transition"
          >
            {theme === "dark" ? <FaSun className="h-5 w-5" /> : theme === "light" ? <FaMoon className="h-5 w-5" /> : <FaAdjust className="h-5 w-5" />}

            {/*isDarkMode ? <FaSun className="h-5 w-5" /> : <FaMoon className="h-5 w-5" />*/}
          </button>

          {/* Conmutador de idiomas */}
          <div ref={languageSwitcherRef}>
            {/* Botón del globo */}
            <button
              className="border-2 border-grayButtonBorder bg-grayButton rounded-lg text-white px-3 py-3 flex items-center justify-center hover:border-white transition"
              onClick={(e) => {
                e.stopPropagation();
                setIsLanguageSelectorOpen((prev) => !prev);
              }}
            >
              <BsGlobe className="h-5 w-5" />
            </button>

            <AnimatePresence>
              {isLanguageSelectorOpen && (
                <motion.div
                  className="fixed inset-0 z-[90] flex items-center justify-center backdrop-blur-[15px] bg-[#38424f17]"
                  onClick={() => setIsLanguageSelectorOpen(false)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div
                    className="bg-[#086cf97b] p-[50px] rounded-[10px] flex flex-col gap-[10px] backdrop-blur-[100px]"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <motion.p
                      className="text-center text-[1.5rem] text-white mb-[24px] mt-0"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      {t('select_language')}
                    </motion.p>

                    <div className="grid grid-cols-3 gap-[20px] justify-items-center">
                      {availableLanguages.map((lang, index) => (
                        <motion.div
                          key={lang.code}
                          onClick={() => {
                            changeLanguage(lang.code);
                            setIsLanguageSelectorOpen(false);
                          }}
                          className="flex flex-col items-center text-white text-[0.9rem] w-[80px] h-[75px] p-2 cursor-pointer rounded-[8px] hover:bg-[#086cf953] transition-colors"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: 0.1 * (index + 1) }}
                        >
                          <img src={lang.icon} alt={lang.code} className="w-[34px] h-[34px] mb-1" />
                          <span>{t(lang.code)}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                </motion.div>
              )}

            </AnimatePresence>
          </div>



        </div>


        {/* Menú hamburguesa (mobile/tablet) */}
        <div className="lg:hidden   flex items-center gap-4 bg-primaryBg dark:bg-blueMenu z-50">
          {/* Conmutador de tema */}
          <button
            onClick={toggleTheme}
            className="border-2 border-grayButtonBorder bg-grayButton rounded-lg text-white px-3 py-3 flex items-center justify-center hover:border-white transition"
          >
            {theme === "dark" ? <FaSun className="h-5 w-5" /> : theme === "light" ? <FaMoon className="h-5 w-5" /> : <FaAdjust className="h-5 w-5" />}

            {/*isDarkMode ? <FaSun className="h-5 w-5" /> : <FaMoon className="h-5 w-5" />*/}
          </button>

          {/* Conmutador de idiomas */}
          <div ref={languageSwitcherRef} className="relative">
            <button
              className="border-2 border-grayButtonBorder bg-grayButton rounded-lg text-white px-3 py-3 flex items-center justify-center hover:border-white transition"
              onClick={() => setIsLanguageSelectorOpen((prev) => !prev)}
            >
              <BsGlobe className="h-5 w-5" />
            </button>
          </div>

          {/* Menú hamburguesa */}
          <button
            onClick={() => setIsMenuOpen((prev) => !prev)}
            className="text-white focus:outline-none"
          >
            {isMenuOpen ? <FaTimes className="h-6 w-6" /> : <FaBars className="h-6 w-6" />}
          </button>
        </div>
      </nav >
      {/* Menú desplegable (mobile/tablet) */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.nav
            className="fixed top-0 right-0 gap-5 w-screen h-screen backdrop-blur-[30px] z-[10] p-5 md:p-6 flex flex-col box-border scrollbar-hide bg-white/30 dark:bg-transparent"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
          >
            <div className="mt-[calc(95px_+_var(--risk-warning-height,0px))] md:pt-[120px] max-h-[calc(100vh_-_60px_-_var(--risk-warning-height,0px))] md:max-h-[calc(100vh_-_80px_-_var(--risk-warning-height,0px))] overflow-y-auto overflow-x-hidden box-border md:px-[20%] lg:px-[20%] scrollbar-hide">

              {/* Home */}
              <p
                className="text-[#070f24] dark:text-white text-lg md:text-xl cursor-pointer pb-5 font-bold"
                onClick={() => {
                  handleNavigate('/');
                  setIsMenuOpen(false);
                }}
              >
                {t('navigation.home')}
              </p>

              {/* Categorías del knowledgebase */}
              <div className="w-full overflow-hidden text-[#070f24] dark:text-white  text-lg md:text-xl cursor-pointer pb-5 flex flex-col items-start">
                <div
                  className="flex items-center justify-between w-full font-bold truncate"
                  onClick={() => setOpenCategories(prev => Object.keys(prev).length ? {} : { all: true })}
                >
                  <span className="truncate">{t('navigation.categories')}</span>
                  {openCategories.all ? <IoIosArrowUp className="text-lg" /> : <IoIosArrowDown className="text-lg" />}
                </div>
                <AnimatePresence>
                  {openCategories.all && (
                    <motion.ul
                      className="pl-2 list-none  dark:text-white w-full pr-2"
                      initial={{ height: 0 }}
                      animate={{ height: 'auto' }}
                      exit={{ height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {categories.filter(category => {
                        if (category.slug === 'api') {
                          const isLocalhost = window.location.hostname === 'localhost';
                          const apiEnabled = userData?.config?.apiEnabled;
                          return isLocalhost || apiEnabled;
                        }
                        const formulasSlugs = ['formulas', 'formules', 'formule', 'formulas-zh'];
                        if (formulasSlugs.includes(category.slug)) {
                          const isLocalhost = window.location.hostname === 'localhost';
                          const formulasEnabled = userData?.config?.formulasEnabled;
                          return isLocalhost || formulasEnabled;
                        }
                        return true;
                      }).map((category) => (
                        <li key={category.slug} className="w-full pt-3">
                          <div
                            className="flex items-center justify-between w-full font-bold cursor-pointer"
                            onClick={() => toggleCategory(category.slug)}
                          >
                            <span className="truncate pr-2">{category.title}</span>
                            <span className="flex-shrink-0">
                              {openCategories[category.slug] ? <IoIosArrowUp /> : <IoIosArrowDown />}
                            </span>
                          </div>
                          <AnimatePresence>
                            {openCategories[category.slug] && (
                              <motion.ul
                                className="pl-2 pt-2 text-sm text-[#070f24] dark:text-white overflow-hidden"
                                initial={{ height: 0 }}
                                animate={{ height: 'auto' }}
                                exit={{ height: 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                {/* Artículos directos de la categoría */}
                                {category.articles && category.articles.map((article) => (
                                  <li
                                    key={article.slug}
                                    className="cursor-pointer hover:text-secondBg py-1"
                                    onClick={() => {
                                      navigate(`/${i18n.language}/${category.slug}/${article.slug}`);
                                      setOpenCategories({});
                                      setIsMenuOpen(false);
                                    }}
                                  >
                                    {article.title}
                                  </li>
                                ))}
                                
                                {/* Subcategorías */}
                                {category.subcategories && category.subcategories.map((subcategory) => (
                                  <li key={subcategory.slug} className="w-full pt-2">
                                    <div
                                      className="flex items-center justify-between w-full font-semibold cursor-pointer text-gray-600 dark:text-gray-300"
                                      onClick={() => toggleCategory(`${category.slug}-${subcategory.slug}`)}
                                    >
                                      <span className="truncate pr-2">{subcategory.title}</span>
                                      <span className="flex-shrink-0">
                                        {openCategories[`${category.slug}-${subcategory.slug}`] ? <IoIosArrowUp /> : <IoIosArrowDown />}
                                      </span>
                                    </div>
                                    <AnimatePresence>
                                      {openCategories[`${category.slug}-${subcategory.slug}`] && (
                                        <motion.ul
                                          className="pl-2 pt-1 text-xs text-[#070f24] dark:text-white overflow-hidden"
                                          initial={{ height: 0 }}
                                          animate={{ height: 'auto' }}
                                          exit={{ height: 0 }}
                                          transition={{ duration: 0.3 }}
                                        >
                                          {subcategory.articles && subcategory.articles.map((article) => (
                                            <li
                                              key={article.slug}
                                              className="cursor-pointer hover:text-secondBg py-1"
                                              onClick={() => {
                                                navigate(`/${i18n.language}/${category.slug}/${subcategory.slug}/${article.slug}`);
                                                setOpenCategories({});
                                                setIsMenuOpen(false);
                                              }}
                                            >
                                              {article.title}
                                            </li>
                                          ))}
                                        </motion.ul>
                                      )}
                                    </AnimatePresence>
                                  </li>
                                ))}
                              </motion.ul>
                            )}
                          </AnimatePresence>
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>

              {/* Excent Capital */}
              <p
                className="text-[#070f24] dark:text-white text-lg md:text-xl cursor-pointer pb-5 font-bold"
                onClick={handleGetMainpage}
              >
                {t('navigation.excentCapital')}
              </p>

              {/* Contact */}
              <p
                className="text-[#070f24] dark:text-white text-lg md:text-xl cursor-pointer pb-5 font-bold"
                onClick={() => {
                  handleNavigate('/contact/');
                  setIsMenuOpen(false);
                }}
              >
                {t('navigation.contact')}
              </p>
            </div>

            {/* Botón de login / open account */}
            <div>
              {!isLoggedIn || !userData ? (
                <div className='flex flex-col gap-4'>
                  <button
                    onClick={handleClick}
                    className="w-full bg-[#086ef9] border-2 border-[#3288ff] text-white text-[0.9rem] py-[12px] rounded-lg hover:border-[#086ef9] transition-colors cursor-pointer"
                    data-cy={dataCy(SELECTORS.BUTTON, 'menu-signup')}
                  >
                    {t('sign-up')}
                  </button>
                  <button
                    onClick={handleGetStartedClickNav}
                    className="w-full bg-[#086ef9] border-2 border-[#3288ff] text-white text-[0.9rem] py-[12px] rounded-lg hover:border-[#086ef9] transition-colors cursor-pointer"
                    data-cy={dataCy(SELECTORS.BUTTON, 'menu-login')}
                  >
                    {t('login')}
                  </button>
                </div>
              ) : (
                <div className="text-white w-full">
                  <p className="text-base font-semibold mb-2">{userData.name}</p>
                  <div className="border-t border-white mb-2 w-full"></div>
                  <div className="mb-3 mt-2">
                    <p className="text-sm font-semibold">{t('email')}</p>
                    <p className="text-sm text-gray-400 break-words">{userData.email}</p>
                  </div>

                  {userData?.role !== "Client" && (
                    <button
                      onClick={handleGetStartedClickBo}
                      className="w-full bg-[#086ef9] border-2 border-[#3288ff] text-white text-[0.9rem] py-2 rounded-lg hover:border-[#086ef9] transition-colors cursor-pointer mb-2"
                    >
                      {t('backoffice')}
                    </button>
                  )}

                  <button
                    onClick={handleClick}
                    className="w-full bg-[#086ef9] border-2 border-[#3288ff] text-white text-[0.9rem] py-2 rounded-lg hover:border-[#086ef9] transition-colors cursor-pointer mb-2"
                  >
                    {t('my-account')}
                  </button>

                  <button
                    className="w-full text-sm text-red-400 border border-red-400 rounded-lg py-2 hover:bg-red-500 hover:text-white transition-colors duration-200 cursor-pointer"
                    onClick={handleLogout}
                  >
                    {t('logout')}
                  </button>
                </div>
              )}
            </div>

          </motion.nav>
        )}
      </AnimatePresence>

      {/* conmutador de idiomas (mobile/tablet) */}
      <AnimatePresence>
        {isLanguageSelectorOpen && (
          <motion.div
            className="fixed top-10 left-0 w-screen h-screen !z-[100] flex justify-center items-center backdrop-blur-[30px] bg-[#38424f17] block md:hidden lg:hidden xl:hidden"
            onClick={() => setIsLanguageSelectorOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="bg-[#086cf97b] backdrop-blur-[100px] p-5 rounded-[10px] flex flex-col gap-4 min-w-[280px] max-w-[320px]"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <motion.h2
                className="text-center text-white font-bold text-[1.2rem]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {t('select_language')}
              </motion.h2>

              <div className="flex flex-col gap-3">
                {availableLanguages.map((lang, index) => (
                  <motion.div
                    key={lang.code}
                    className="flex items-center gap-3 text-white cursor-pointer p-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                    onClick={() => {
                      changeLanguage(lang.code);
                      setIsLanguageSelectorOpen(false); // Cierra el selector
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.05 * (index + 1) }}
                  >
                    <img src={lang.icon} alt={lang.label} className="w-[30px] h-[30px]" />
                    <span>{lang.label}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>



    </>

  );


};

export default Menu;
